import { AfterViewInit, Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { SocialAuthService } from '@abacritt/angularx-social-login';
import { FacebookLoginProvider, GoogleLoginProvider } from '@abacritt/angularx-social-login';
import { ToastrService } from 'ngx-toastr';

import { AuthenticationService, CompanyService, Auth0Service, ProfileService } from '../_services';
import { ChatbotStateService } from 'app/chatbot/services/chatbot-state.service';
import { getErrorMessage, resolvePostLoginUrl, loadAppleAuthScript, clearLegacyGoto } from 'app/_utils/helpers';
import { BaseComponent } from 'app/_components/base.component';
import { LOGIN_GOTO_QUERY_PARAM } from 'app/app.constants';
import { environment } from 'environments/environment';

declare var AppleID: any;

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent extends BaseComponent implements OnInit, AfterViewInit {
  model: any = {};
  loading = false;
  error = '';
  showPassword = false;
  accountType: 'personal' | 'corporate' = 'corporate';
  accountTypes = [
    { label: 'Personal Account', value: 'personal' },
    { label: 'Corporate Account', value: 'corporate' },
  ];

  handleLogin = (result) => {
    this.finishLogin(result);
  }

  handleError = (err) => {
    this.loading = false;
    this.error = getErrorMessage(err);
  }

  constructor(
    router: Router,
    authenticationService: AuthenticationService,
    companyService: CompanyService,
    private route: ActivatedRoute,
    private socialAuthService: SocialAuthService,
    private toastr: ToastrService,
    private auth0Service: Auth0Service,
    private chatbotStateService: ChatbotStateService,
    private profileService: ProfileService,
  ) {
    super(router, authenticationService, companyService);
  }

  getGotoUrl() {
    return resolvePostLoginUrl(this.route.snapshot.queryParams[LOGIN_GOTO_QUERY_PARAM]);
  }

  /** Confirm the API accepts the token before leaving the login page. */
  private finishLogin(result: boolean) {
    if (result === false) {
      this.loading = false;
      this.error = 'Unable to sign in with this account.';
      return;
    }
    this.chatbotStateService.clearState();
    this.profileService.resetLoaded();
    this.profileService.getProfile().subscribe({
      next: () => {
        this.loading = false;
        this.error = '';
        clearLegacyGoto();
        this.router.navigateByUrl(this.getGotoUrl());
      },
      error: (err) => {
        this.loading = false;
        this.authenticationService.logout(true);
        this.error = getErrorMessage(err) || 'Session could not be established. Please try again.';
      },
    });
  }

  private resumeExistingSession() {
    if (!this.authenticationService.isSessionValid()) {
      this.authenticationService.logout(true);
      return;
    }
    this.profileService.resetLoaded();
    this.profileService.getProfile().subscribe({
      next: () => this.router.navigateByUrl(this.getGotoUrl()),
      error: () => this.authenticationService.logout(true),
    });
  }

  ngOnInit() {
    clearLegacyGoto();

    if (this.route.snapshot.queryParams['session'] === 'expired') {
      this.error = 'Your session expired while you were away. Please sign in again.';
    }

    if (this.authenticationService.isSessionValid()) {
      this.resumeExistingSession();
    } else {
      this.socialAuthService.authState.subscribe((user) => {
        if (user.provider === GoogleLoginProvider.PROVIDER_ID) {
          this.authenticationService.loginWithGoogle({
            token: user.idToken,
          }).subscribe(this.handleLogin, this.handleError);
        }
        if (user.provider === FacebookLoginProvider.PROVIDER_ID) {
          this.authenticationService.loginWithFacebook({
            token: user.authToken,
          }).subscribe(this.handleLogin, this.handleError);
        }
      });
      this.auth0Service.parseHash((err, authResult) => {
        if (authResult && authResult.accessToken && authResult.idToken) {
          this.authenticationService.loginWithAuth0({
            token: authResult.idToken,
          }).subscribe(this.handleLogin, this.handleError);
        } else if (err) {
          console.log(err);
          const pathOnly = this.router.url.split('?')[0].split('#')[0];
          if (pathOnly !== '/login') {
            this.router.navigate(['/login']);
          }
        }
      });
    }
  }

  ngAfterViewInit() {
    loadAppleAuthScript(() => {
      AppleID.auth.init({
        clientId: environment.APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: environment.APPLE_REDIRECT_URI,
        usePopup: true
      });
    });
  }

  parseJwt(token) {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  };

  async signinWithApple() {
    try {
      const data = await AppleID.auth.signIn();
      this.authenticationService.loginWithApple(data).subscribe(this.handleLogin, this.handleError);
    } catch (err) {
      console.log(err);
    }
  }

  signinWithAuth0() {
    this.auth0Service.login();
  }

  signinWithFacebook() {
    this.socialAuthService.signIn(FacebookLoginProvider.PROVIDER_ID);
  }

  login() {
    this.loading = true;
    // Drop any leftover JWT so sign-in is never blocked by a stale session.
    this.authenticationService.logout(true);
    this.authenticationService.login(this.model)
      .subscribe(this.handleLogin, this.handleError);
  }
}
